import { useCallback, useState, useSyncExternalStore } from 'react';

// A render-safe clock: the epoch-ms timestamp read once at mount and
// re-read every `intervalMs` — the ticking `now` that time-derived render
// state (countdowns, lifecycle statuses, relative labels) takes instead of
// an impure Date.now() in render, which the React Compiler would memoize
// away with the label. `active` false arms no timer and freezes the value;
// turning it back on re-reads the clock at once, so a surface that paused
// while hidden reopens on the current time. The interval is cleared on
// unmount.
export function useNow(intervalMs: number, active = true): number {
  const [clock] = useState(() => createClock());
  // eslint-disable-next-line no-restricted-syntax -- useSyncExternalStore resubscribes (and re-reads the clock) whenever this function's identity changes
  const subscribe = useCallback(
    (onChange: () => void) => {
      if (!active) return () => undefined;
      // React re-reads the snapshot right after subscribing, so the refresh
      // alone re-renders a reactivated surface on the current time.
      clock.refresh();
      const id = window.setInterval(() => {
        clock.refresh();
        onChange();
      }, intervalMs);
      return () => window.clearInterval(id);
    },
    [clock, intervalMs, active],
  );
  return useSyncExternalStore(subscribe, clock.read, clock.read);
}

function createClock(): { read: () => number; refresh: () => void } {
  let now = Date.now();
  return {
    read: () => now,
    refresh: () => {
      now = Date.now();
    },
  };
}
